/*!
 * Dalafisk – product page enhancements
 * ------------------------------------------------------------------
 * One job: turn Pinnacle's flat list of variant radio buttons into the design's
 * `.pd-stlk` pill row — a second line on each pill, and a "Fler storlekar"
 * button once there are more than three.
 *
 * WHY JAVASCRIPT AND NOT LIQUID. The pills are emitted by snippets/product-option.liquid,
 * which is rendered from the shared 33 KB product-information-blocks.liquid. Reaching
 * them in Liquid would mean forking that file — a third of Pinnacle's product
 * chain — to change two lines of markup. Everything this script needs is already
 * server-rendered: `<variant-selects>` carries `<script type="application/json">`
 * with the full variant list (Pinnacle puts it there for its own picker).
 *
 * WHAT THE SECOND LINE IS. The design's pill reads "400 g" over "5 × 80 g" —
 * a weight and a pack composition. Shopify models the weight (`variant.weight`,
 * always in grams in the JSON) and does NOT model pack composition, so the
 * weight is what is shown and the merchant's own option value stays the primary
 * label. No string is parsed and nothing is inferred from naming, so this is
 * correct on every product: where weights are unset, the pill is simply one line.
 *
 * FAILURE MODE IS THE CURRENT STATE. Collapsing is done here, not in CSS, so a
 * failed or absent script leaves every pill visible and selectable — never a row
 * of options hidden behind a button that never appears.
 */
(function () {
  'use strict';

  if (window.__dalafiskProduct) return;
  window.__dalafiskProduct = true;

  var VISIBLE = 3;

  /* Swedish decimal comma, and kg once it reads better than grams. Mirrors the
     design's own "400 g" / "1,12 kg" / "3,2 kg". */
  function formatWeight(grams) {
    if (!grams || grams <= 0) return '';
    if (grams < 1000) return String(Math.round(grams)) + ' g';
    var kg = grams / 1000;
    var s = (Math.round(kg * 100) / 100).toString().replace('.', ',');
    return s + ' kg';
  }

  function variantsFor(selects) {
    var tag = selects.querySelector('script[type="application/json"]');
    if (!tag) return null;
    try { return JSON.parse(tag.textContent); } catch (e) { return null; }
  }

  /* The weight of an option value, but ONLY when every variant carrying that
     value agrees. On a two-option product "Blå" spans several sizes with
     different weights, and printing one of them would be a lie. */
  function weightForValue(variants, optionIndex, value) {
    var key = 'option' + optionIndex;
    var found = null;
    for (var i = 0; i < variants.length; i++) {
      if (variants[i][key] !== value) continue;
      var w = variants[i].weight;
      if (!w) return 0;
      if (found === null) found = w;
      else if (found !== w) return 0;
    }
    return found || 0;
  }

  function enhance(fieldset, variants) {
    var index = parseInt((fieldset.dataset.index || '').replace('option', ''), 10);
    if (!index) return;

    var inputs = fieldset.querySelectorAll('input[type="radio"]');
    if (inputs.length <= 1) return;

    /* The button's text is built in Liquid — it knows the heaviest variant and
       the shop's weight unit, and it keeps the string translatable. */
    var labels = document.querySelector('[data-dalafisk-more-label]');
    var labelMore = (labels && labels.getAttribute('data-dalafisk-more-label')) || 'Fler storlekar';
    var labelSub = (labels && labels.getAttribute('data-dalafisk-more-sub')) || '';

    var items = [];

    Array.prototype.forEach.call(inputs, function (input) {
      var label = fieldset.querySelector('label[for="' + (window.CSS && CSS.escape ? CSS.escape(input.id) : input.id) + '"]');
      if (!label) return;
      items.push({ input: input, label: label });

      if (!variants) return;
      var text = formatWeight(weightForValue(variants, index, input.value));
      if (!text || label.querySelector('.dalafisk-variant__meta')) return;
      var small = document.createElement('small');
      small.className = 'dalafisk-variant__meta';
      small.textContent = text;
      label.appendChild(small);
    });

    if (items.length <= VISIBLE) return;

    /* Never collapse past the visitor's current choice — landing on a product
       whose selected size is the 6th would otherwise hide what is selected. */
    var selectedIndex = -1;
    items.forEach(function (it, i) { if (it.input.checked) selectedIndex = i; });
    if (selectedIndex >= VISIBLE) return;

    var hidden = items.slice(VISIBLE);
    hidden.forEach(function (it) {
      it.input.setAttribute('data-dalafisk-collapsed', '');
      it.label.setAttribute('data-dalafisk-collapsed', '');
    });

    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'dalafisk-variant__more';
    /* Same two-line shape as a size pill, so the button sits in the grid as the
       fourth cell rather than reading as a separate bar. */
    var strong = document.createElement('b');
    strong.textContent = labelMore;
    button.appendChild(strong);
    if (labelSub) {
      var sub = document.createElement('span');
      sub.textContent = labelSub;
      button.appendChild(sub);
    }
    button.setAttribute('aria-expanded', 'false');

    button.addEventListener('click', function () {
      hidden.forEach(function (it) {
        it.input.removeAttribute('data-dalafisk-collapsed');
        it.label.removeAttribute('data-dalafisk-collapsed');
      });
      button.setAttribute('aria-expanded', 'true');
      button.remove();
      /* Focus the first revealed pill so keyboard users are not dropped at the
         end of the document after the button disappears. */
      if (hidden[0]) hidden[0].label.focus({ preventScroll: true });
    });

    var last = items[items.length - 1].label;
    last.parentNode.insertBefore(button, last.nextSibling);
  }

  /* ------------------------------------------------------------------
     THE DELIVERY-DAY WRITER IS GONE — DO NOT RE-ADD IT. This used to fill the
     first promise row (the design's `#pd-leverans`) with "Leverans <weekday>
     <day> <month>", computed in the browser because a Liquid-rendered date
     would be the date the page was last cached. The shop does not want to
     promise an exact delivery date, so the row itself was removed from
     `snippets/dalafisk-product-promises.liquid` and this code with it — there
     is no `[data-dalafisk-delivery-day]` element on the page any more.
     ------------------------------------------------------------------ */

  /* ------------------------------------------------------------------
     `.pd-narings` — the nutrition cells in the first dropdown.

     THE PROBLEM IS THE SHAPE OF THE DATA, NOT THE AMOUNT OF IT. The shop keeps
     nutrition in ONE rich-text metafield (`custom.nutritional`) and the values
     were typed into it as alternating label and <strong>, inside a single <p>,
     with no separators at all:

       <p>Energi<strong>649kJ/155kcal</strong>Fett<strong>7,9g</strong>...</p>

     which renders as "Energi649kJ/155kcalFett7,9gVarav mätat fett0g..." — one
     unreadable run. CSS cannot fix it: the labels are bare text nodes, and
     there is nothing for a rule to select.

     So the pairs are rebuilt here into the design's grid of cells. This is a
     rendering change only — every label and every value is the merchant's own
     text, moved, not edited.

     IT ONLY RUNS ON DATA IT RECOGNISES: strictly alternating text/<strong>,
     both non-empty, at least two pairs. A merchant who writes the field as a
     table, a list, or prose fails that test and the field is left exactly as it
     came. Same principle as the variant pills — never parse a merchant's
     sentence, only restructure what is already structured.
     ------------------------------------------------------------------ */
  function nutritionGrid(scope) {
    var body = scope.querySelector('.dalafisk-product__details-body .metafield-rich_text_field');
    if (!body) return;

    /* An empty heading is what the metafield editor leaves behind when the
       merchant clears the title; it renders as a blank line. */
    var heads = body.querySelectorAll('h1, h2, h3, h4');
    Array.prototype.forEach.call(heads, function (h) {
      if (!h.textContent.trim()) h.remove();
    });

    var para = body.querySelector('p');
    if (!para || para.querySelector('table, ul, ol, dl')) return;

    var pairs = [];
    var label = null;
    var ok = true;

    Array.prototype.forEach.call(para.childNodes, function (node) {
      if (!ok) return;
      if (node.nodeType === 3) {
        var text = node.textContent.trim();
        if (!text) return;
        /* two labels in a row means this is prose, not pairs */
        if (label !== null) { ok = false; return; }
        label = text;
      } else if (node.nodeName === 'STRONG' || node.nodeName === 'B') {
        var value = node.textContent.trim();
        if (label === null || !value) { ok = false; return; }
        pairs.push([label, value]);
        label = null;
      } else if (node.nodeName !== 'BR') {
        ok = false;
      }
    });

    if (!ok || label !== null || pairs.length < 2) return;

    var dl = document.createElement('dl');
    dl.className = 'dalafisk-product__nutrition';
    pairs.forEach(function (pair) {
      var cell = document.createElement('div');
      var dt = document.createElement('dt');
      var dd = document.createElement('dd');
      dt.textContent = pair[0];
      dd.textContent = pair[1];
      cell.appendChild(dt);
      cell.appendChild(dd);
      dl.appendChild(cell);
    });
    para.parentNode.replaceChild(dl, para);
  }

  function init() {
    var scope = document.querySelector('.dalafisk-product-section');
    if (!scope) return;
    var selects = scope.querySelector('variant-selects');
    var variants = selects ? variantsFor(selects) : null;
    var fieldsets = scope.querySelectorAll('.product-form__input--block');
    Array.prototype.forEach.call(fieldsets, function (f) { enhance(f, variants); });
    nutritionGrid(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
}());
